import {
   Flex,
   FlexProps,
   SystemStyleObject,
   useBreakpointValue,
   useMergeRefs,
} from '@chakra-ui/react';
import { useRef, useLayoutEffect, forwardRef, useState } from 'react';
import { easeInOutQuad } from './easing';

type BeforeAfterOpacityState = {
   after: SystemStyleObject;
   before: SystemStyleObject;
};

const gradientWidths = { base: 40, sm: 50, md: 70, lg: 100 };

const sharedStyle: SystemStyleObject = {
   content: '""',
   position: 'absolute',
   height: '100%',
   pointerEvents: 'none',
   zIndex: 9999,
};

const beforeStyle: SystemStyleObject = {
   ...sharedStyle,
   left: '-1px',
   background: 'linear-gradient(270deg, rgba(249, 250, 251, 0) 0%, #F9FAFB 106.41%)',
};

const afterStyle: SystemStyleObject = {
   ...sharedStyle,
   right: '-1px',
   background: 'linear-gradient(90deg, rgba(249, 250, 251, 0) 0%, #F9FAFB 106.41%)',
};

function getGradients(): {
   before: SystemStyleObject;
   after: SystemStyleObject;
   gradientWidthPX: number;
} {
   const gradientWidthPX = useBreakpointValue(gradientWidths, { ssr: false });
   return {
      before: { ...beforeStyle, width: `${gradientWidthPX}px` },
      after: { ...afterStyle, width: `${gradientWidthPX}px` },
      gradientWidthPX,
   };
}

export const FlexScrollGradient = forwardRef(function FlexScrollGradient(props: FlexProps, ref) {
   const internalRef = useRef<HTMLDivElement>();
   const refs = useMergeRefs(internalRef, ref);

   const [flexScrollState, setFlexScrollState] = useState<BeforeAfterOpacityState>({
      before: { opacity: 0 },
      after: { opacity: 0 },
   });

   const { before, after, gradientWidthPX } = getGradients();
   const doMeasure = (el: HTMLElement) => {
      const { scrollWidth, clientWidth, scrollLeft, clientHeight } = el;

      const isScrollable = scrollWidth > clientWidth;
      if (!isScrollable) {
         setFlexScrollState(() => {
            return {
               before: { opacity: 0, height: 0 },
               after: { opacity: 0, height: 0 },
            };
         });
         return;
      }

      const scrollPosition = scrollLeft + clientWidth;
      const afterStart = scrollWidth - gradientWidthPX;
      const height = `${clientHeight}px`;

      const beforeGradientShownPercent =
         scrollLeft < gradientWidthPX ? scrollLeft / gradientWidthPX : 1;

      const afterGradientShownPercent =
         scrollPosition >= afterStart ? 1 - (scrollPosition - afterStart) / gradientWidthPX : 1;

      const beforeGradientOpacity = easeInOutQuad(Math.max(0, beforeGradientShownPercent));
      const afterGradientOpacity = easeInOutQuad(Math.max(0, afterGradientShownPercent));

      setFlexScrollState(() => {
         return {
            before: { opacity: beforeGradientOpacity, height },
            after: { opacity: afterGradientOpacity, height },
         };
      });
   };

   useLayoutEffect(() => {
      if (!internalRef.current) {
         return;
      }

      const measure = () => {
         doMeasure(internalRef.current);
      };

      measure();

      internalRef.current.addEventListener('scroll', measure);
      window.addEventListener('resize', measure);

      return () => {
         internalRef.current?.removeEventListener('scroll', measure);
         window.removeEventListener('resize', measure);
      };
   }, [internalRef?.current]);

   const newBefore = { ...before, ...flexScrollState.before };
   const newAfter = { ...after, ...flexScrollState.after };

   return (
      <Flex {...props} position="relative" isolation="isolate">
         <Flex
            ref={refs}
            overflow="auto"
            _before={newBefore}
            _after={newAfter}
            sx={{ ...props.sx }}
         >
            {props.children}
         </Flex>
      </Flex>
   );
});
